import { twJoin } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useMemo } from 'react';

import { bodyText } from '@/styles/styles';
import { TempObjective } from '@/models/CoursePath';
import SecondaryInput from '@/components/ui/custom/secondary/SecondaryInput';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { StudioPermissions } from '@/enums/VersioningStatus';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { CourseStudioProps } from '../info/CourseStudioInfo.hooks';
import { CourseStudioFormFields } from '../CourseStudio.hooks';
import { useAutoFocus } from '@/data/hook/useAutoFocus';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import SecondaryTextArea from '@/components/ui/custom/secondary/SecondaryTextArea';

type Props = CourseStudioProps & {
  studioPermissions: StudioPermissions;
};

export default function CourseStudioObjectivesStep(props: Props) {
  const { studioPermissions } = props;

  const { control } = useFormContext<CourseStudioFormFields>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'objectives',
    keyName: 'tempId',
  });

  const { t } = useTranslation('translation', {
    keyPrefix: 'courseStudio',
  });

  const { setElementRef } = useAutoFocus(fields.length);

  const simpleOrAdvancedEditing = useMemo(
    () =>
      !studioPermissions.allowOtherSteps.simpleEditing &&
      !studioPermissions.allowOtherSteps.advancedEditing,
    [studioPermissions],
  );

  const handleAddObjective = () => {
    const newObjective = new TempObjective();
    append(newObjective);
  };

  return (
    <article className="flex flex-col gap-2.5">
      <p className={twJoin(bodyText, 'text-neutral')}>{t('shouldBeAbleTo')}</p>

      <ul
        className={twJoin(
          'flex flex-col gap-2.5 max-h-[calc(100vh-24rem)]',
          scrollBarYClassName,
        )}
      >
        {fields.map((field, index) => (
          <li key={field.id || field.tempId} className="flex flex-col gap-2.5">
            <Controller
              control={control}
              name={`objectives.${index}.name`}
              render={({ field: { onChange, value = '' } }) => (
                <ConditionalWrapper
                  condition={simpleOrAdvancedEditing}
                  wrapper={
                    <Tooltip
                      text={t('notAllowedIfNotAtLeastInLimitedEdition')}
                      classNameContainer="flex w-full"
                    />
                  }
                >
                  <SecondaryInput
                    ref={setElementRef(index)}
                    value={value}
                    placeholder={t('addAnObjective')}
                    onChange={onChange}
                    disabled={simpleOrAdvancedEditing}
                    clearWrapper={
                      <ConditionalWrapper
                        condition={
                          !studioPermissions.allowOtherSteps.advancedEditing
                        }
                        wrapper={
                          <Tooltip
                            text={t('notAllowedLimitedEditing')}
                            classNameContainer="flex"
                          />
                        }
                      >
                        <IconButton
                          disabled={
                            !studioPermissions.allowOtherSteps.advancedEditing
                          }
                          type="button"
                          icon={
                            <TrashIcon className="text-error w-5 h-5 stroke-[1.5] hover:text-error/50 transition-all duration-200 ease-in-out" />
                          }
                          onClick={() => remove(index)}
                        />
                      </ConditionalWrapper>
                    }
                  />
                </ConditionalWrapper>
              )}
            />
            <Controller
              control={control}
              name={`objectives.${index}.description`}
              render={({ field: { onChange, value = '' } }) => (
                <ConditionalWrapper
                  condition={simpleOrAdvancedEditing}
                  wrapper={
                    <Tooltip
                      text={t('notAllowedIfNotAtLeastInLimitedEdition')}
                      classNameContainer="flex w-full"
                    />
                  }
                >
                  <SecondaryTextArea
                    value={value || ''}
                    onChange={value => onChange(value)}
                    disabled={simpleOrAdvancedEditing}
                    className={{ input: 'min-h-8', base: 'min-h-8' }}
                  />
                </ConditionalWrapper>
              )}
            />
            <hr className="border-base-content/25" />
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        <ConditionalWrapper
          condition={!studioPermissions.allowOtherSteps.advancedEditing}
          wrapper={
            <Tooltip
              text={t('notAllowedLimitedEditing')}
              classNameContainer="flex"
            />
          }
        >
          <MainButton
            disabled={!studioPermissions.allowOtherSteps.advancedEditing}
            color="custom"
            className="text-primary font-500 capitalize"
            icon={<PlusIcon />}
            text={t('addObjective')}
            onClick={handleAddObjective}
          />
        </ConditionalWrapper>
      </div>
    </article>
  );
}
